import * as Sentry from "@sentry/react";
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useConvexAuth } from "convex/react";
import { lazy, Suspense, useEffect } from "react";
import { AppHeader } from "@/components/app-header";
import { ApplicationVersionErrorFallback } from "@/components/application-version-error-fallback";
import { AuthErrorBoundary } from "@/components/auth-error-boundary";
import { CollapsedSidebar } from "@/components/collapsed-sidebar";
import { CopyPageUrlShortcut } from "@/components/copy-page-url-shortcut";
import { GlobalNotFound } from "@/components/global-not-found";
import { LeftSidebar } from "@/components/left-sidebar";
import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import { MobileHeader } from "@/components/mobile-header";
import { NetworkStatusBanner } from "@/components/network-status-banner";
import { PostHogContextSync } from "@/components/posthog-context-sync";
import { SentryContextSync } from "@/components/sentry-context-sync";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/ui/loading-state";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/spinner";
import { StatusPanel } from "@/components/ui/status-panel";
import { TooltipProvider } from "@/components/ui/tooltip";
import { OrganizationProvider } from "@/contexts/organization-context";
import { TabsProvider, useTabsState } from "@/contexts/tabs-context";
import { CommandPaletteProvider } from "@/features/command-palette";
import { ProjectDragProvider } from "@/features/home/contexts/project-drag-context";
import { ProjectSidebarsHost } from "@/features/project/components/project-sidebars-host";
import { ProjectsHost } from "@/features/project/components/projects-host";
import { SettingsSidebar } from "@/features/settings";
import { SettingsOriginProvider } from "@/features/settings/settings-origin-context";
import { isChunkLoadError } from "@/lib/connection-errors";
import { isAuthError } from "@/lib/convex-errors";
import { matchWorkspaceLocation } from "@/routing/core/locations";
import { KeepAliveContainer } from "@/routing/keep-alive/KeepAliveContainer";
import { RouterBridge } from "@/routing/react/RouterBridge";
import { decideRootBeforeLoad } from "@/routing/root-before-load";

export interface RouterContext {
  auth: {
    isAuthenticated: boolean;
    isLoading: boolean;
  };
}

const LazyAdminWorkspaceHost = lazy(() =>
  import("@/features/df-admin/components/admin-workspace-host").then((module) => ({
    default: module.AdminWorkspaceHost,
  }))
);
const LazyAdminWorkspaceSidebarHost = lazy(() =>
  import("@/features/df-admin/components/admin-workspace-sidebar-host").then((module) => ({
    default: module.AdminWorkspaceSidebarHost,
  }))
);

/**
 * Loading component shown while auth state is being determined.
 * Prevents flash of protected content.
 */
function AuthLoadingScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <div className="text-center">
        <Spinner className="size-8 text-primary mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    </div>
  );
}

function useShouldMountAdminWorkspace() {
  const { tabs } = useTabsState();
  const routerState = useRouterState();
  const activeWorkspace = matchWorkspaceLocation(routerState.location.pathname);
  return activeWorkspace?.kind === "admin" || tabs.some((tab) => tab.kind === "admin");
}

function LazyAdminWorkspaceSidebarMount() {
  const shouldMountAdminWorkspace = useShouldMountAdminWorkspace();
  if (!shouldMountAdminWorkspace) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <LazyAdminWorkspaceSidebarHost />
    </Suspense>
  );
}

function AdminWorkspaceLoadingFallback() {
  return (
    <main className="flex flex-1 items-center justify-center">
      <LoadingState showSpinner message="Loading admin..." />
    </main>
  );
}

function AdminWorkspaceLoadFailure({ error }: { error: unknown }) {
  if (isChunkLoadError(error)) {
    return <ApplicationVersionErrorFallback />;
  }

  return (
    <main className="flex flex-1 items-center justify-center">
      <StatusPanel
        tone="destructive"
        title="The admin workspace failed to load."
        description="Reloading the application restores it. Other pages are unaffected."
        actions={
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      />
    </main>
  );
}

function LazyAdminWorkspaceMount() {
  const shouldMountAdminWorkspace = useShouldMountAdminWorkspace();
  if (!shouldMountAdminWorkspace) {
    return null;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => {
        // Auth errors must reach the root AuthErrorBoundary to re-authenticate;
        // don't swallow them in the admin shell backstop. The per-tab
        // AdminWorkspaceErrorBoundary re-throws auth errors up to here.
        if (isAuthError(error)) {
          throw error;
        }
        return <AdminWorkspaceLoadFailure error={error} />;
      }}
      beforeCapture={(scope) => scope.setTag("feature", "df-admin-shell")}
    >
      <Suspense fallback={<AdminWorkspaceLoadingFallback />}>
        <LazyAdminWorkspaceHost />
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}

/**
 * Root layout component - main app shell.
 *
 * This entire app requires authentication (except /auth/* routes).
 * The root layout handles:
 * - Auth loading state (shows spinner while checking authentication)
 * - Full app shell for authenticated routes (header, tabs, project host)
 * - Minimal layout for auth routes (just centered Outlet)
 *
 * Authentication is enforced in beforeLoad for all non-/auth/* routes.
 */
function RootLayout() {
  const routerState = useRouterState();
  const { isLoading, isAuthenticated } = useConvexAuth();
  const navigate = useNavigate();

  const isAuthRoute = routerState.location.pathname.startsWith("/auth");
  const isErrorRoute = routerState.location.pathname.startsWith("/error");
  const isSettingsRoute = routerState.location.pathname.startsWith("/settings");
  const activeWorkspace = matchWorkspaceLocation(routerState.location.pathname);
  const isProjectRoute = activeWorkspace?.kind === "project";
  const isAdminRoute = activeWorkspace?.kind === "admin";
  const isWorkspaceRoute = activeWorkspace !== null;

  // Redirect to login when auth finishes loading and user is not authenticated
  // This handles the case where beforeLoad ran while auth was still loading
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isAuthRoute && !isErrorRoute) {
      navigate({
        to: "/auth/login",
        search: { redirect: routerState.location.href },
      });
    }
  }, [isLoading, isAuthenticated, isAuthRoute, isErrorRoute, navigate, routerState.location.href]);

  // Show loading screen while auth is initializing (but not on auth/error routes)
  // This prevents flash of protected content
  if (isLoading && !isAuthRoute && !isErrorRoute) {
    return <AuthLoadingScreen />;
  }

  // Also show loading screen if not authenticated (redirect is in progress)
  if (!isAuthenticated && !isAuthRoute && !isErrorRoute) {
    return <AuthLoadingScreen />;
  }

  // For auth and error routes, render minimal layout (just centered Outlet)
  // Note: NetworkStatusBanner handles its own route-based visibility
  if (isAuthRoute || isErrorRoute) {
    return (
      <>
        <NetworkStatusBanner />
        <Outlet />
        <TanStackRouterDevtools
          position="bottom-right"
          initialIsOpen={false}
          toggleButtonProps={{ style: { opacity: 0, width: 0, height: 0, overflow: "hidden" } }}
        />
      </>
    );
  }

  // For authenticated routes, render full app shell.
  // Nested route/content boundaries may handle their own failures; structured
  // auth errors that reach this boundary redirect to login.
  return (
    <AuthErrorBoundary>
      <OrganizationProvider>
        <SentryContextSync />
        <PostHogContextSync />
        <TabsProvider>
          <SettingsOriginProvider>
            <CommandPaletteProvider>
              <CopyPageUrlShortcut enabled={isAuthenticated} />

              <RouterBridge />

              <TooltipProvider>
                <NetworkStatusBanner />
                <SidebarProvider defaultOpen={true} className="h-dvh bg-background">
                  <ProjectDragProvider>
                    {/*
                Left Sidebar - Full height, spans from top to bottom.
                Both LeftSidebar and ProjectSidebarsHost stay always mounted
                (keep-alive pattern) so switching between home and project
                routes is instant with no re-render or data refetch.

                KeepAliveContainer prevents inactive sidebars from mounting on
                mobile, where Sheets are portaled and bypass CSS display:none.
                On desktop, CSS display toggling handles visibility as before.
              */}
                    <KeepAliveContainer
                      active={!isWorkspaceRoute && !isSettingsRoute}
                      variant="contents"
                      mobileUnmount
                    >
                      <LeftSidebar />
                    </KeepAliveContainer>
                    <KeepAliveContainer active={isSettingsRoute} variant="contents" mobileUnmount>
                      <SettingsSidebar />
                    </KeepAliveContainer>
                    <KeepAliveContainer active={isAdminRoute} variant="contents" mobileUnmount>
                      <LazyAdminWorkspaceSidebarMount />
                    </KeepAliveContainer>
                    <KeepAliveContainer active={isProjectRoute} variant="contents" mobileUnmount>
                      <ProjectSidebarsHost />
                    </KeepAliveContainer>

                    {/* Collapsed sidebar — full-height icon strip when sidebar is collapsed */}
                    <CollapsedSidebar isProjectRoute={isProjectRoute} />

                    {/* Main Content Area - To the right of sidebar / collapsed strip */}
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden pb-16 md:pb-0">
                      {/* Mobile Header - hamburger + title + user menu (mobile only) */}
                      <MobileHeader />

                      {/* Tab Bar - Positioned to the right of sidebar (desktop only) */}
                      <AppHeader />

                      {/* Main content */}
                      <div className="flex min-h-0 flex-1 overflow-hidden">
                        <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                          {/*
                    ProjectsHost renders all open project tabs (content only).
                    Sidebars are rendered separately by ProjectSidebarsHost above.
                  */}
                          <ProjectsHost />
                          <LazyAdminWorkspaceMount />

                          {/*
                    Outlet renders non-project routes (home page, settings, etc.).
                    Always mounted (keep-alive) so switching home ↔ project is instant.
                    CSS display toggles visibility; ProjectsHost handles project content.
                  */}
                          <div
                            style={{ display: isWorkspaceRoute ? "none" : "flex" }}
                            className="flex min-h-0 flex-1 overflow-hidden"
                          >
                            <Outlet />
                          </div>
                        </main>
                      </div>
                    </div>

                    {/* Mobile Bottom Navigation (mobile only) */}
                    <MobileBottomNav />
                  </ProjectDragProvider>
                </SidebarProvider>
              </TooltipProvider>
              <TanStackRouterDevtools
                position="bottom-right"
                initialIsOpen={false}
                toggleButtonProps={{
                  style: { opacity: 0, width: 0, height: 0, overflow: "hidden" },
                }}
              />
            </CommandPaletteProvider>
          </SettingsOriginProvider>
        </TabsProvider>
      </OrganizationProvider>
    </AuthErrorBoundary>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: GlobalNotFound,
  beforeLoad: async ({ location, context }) => {
    const decision = decideRootBeforeLoad(location, context.auth);
    if (decision.type === "allow") {
      return;
    }

    if (decision.type === "reload_document") {
      throw redirect({
        href: decision.href,
        reloadDocument: true,
      });
    }

    throw redirect({
      to: "/auth/login",
      search: {
        redirect: decision.redirectHref,
      },
    });
  },
  component: RootLayout,
});
